import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import Button from 'components/Button/Button';
import ImpactScenariosEmptyState from 'components/ImpactScenariosSidebar/components/ImpactScenariosEmptyState/ImpactScenariosEmptyState';
import ImpactScenariosList from 'components/ImpactScenariosSidebar/components/ImpactScenariosList/ImpactScenariosList';
import SidePanel from 'components/SidePanel/SidePanel';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import TextField from 'components/TextField/TextField';
import { BUTTON_VARIANT } from 'constants/interfaces';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';

import type IImpactScenariosSidebar from './interfaces/IImpactScenariosSidebar';

import styles from './sass/ImpactScenariosSidebar.module.scss';

function ImpactScenariosSidebar({
  open,
  scenarios,
  isLoading,
  isCreating,
  isDeleting,
  onCreateAnalysis,
  onOpenAnalysis,
  onDeleteAnalysis,
  onClose,
}: IImpactScenariosSidebar): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<{ id: number, valueChainMapId: number } | null>(null);

  const filteredScenarios = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return scenarios;
    }

    return scenarios.filter((scenario) => scenario.name.toLowerCase().includes(normalizedSearch));
  }, [scenarios, search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleStartNewAnalysis = async () => {
    await onCreateAnalysis();
  };

  const handleDeleteClick = (
    scenarioValueChainMapId: number,
    id: number,
  ) => {
    setPendingDeleteId({ id, valueChainMapId: scenarioValueChainMapId });
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return;
    await onDeleteAnalysis(pendingDeleteId.valueChainMapId, pendingDeleteId.id);
    setPendingDeleteId(null);
  };

  const handleDeleteReject = () => {
    setPendingDeleteId(null);
  };
  const hasScenarios = scenarios.length > 0;
  const isEmptyStateVisible = !isLoading && !hasScenarios;
  const isScenarioListVisible = !isLoading && hasScenarios;

  return (
    <SidePanel
      open={open}
      className={styles.container}
      ariaModal
      role="dialog"
      ariaLabel={t('valueChainMap.impactScenariosTitle')}
    >
      <div className={styles.header}>
        <span className={styles.headerTitle}>{t('valueChainMap.impactScenariosTitle')}</span>
      </div>

      <div className={styles.content}>
        {isLoading ? (
          <div
            className={styles.loaderWrapper}
            role="status"
            aria-live="polite"
          >
            <SmallLoader size={30} />
          </div>
        ) : null}

        {isEmptyStateVisible ? (
          <ImpactScenariosEmptyState
            isCreating={isCreating}
            onStartNewAnalysis={handleStartNewAnalysis}
          />
        ) : null}

        {isScenarioListVisible ? (
          <>
            <TextField
              value={search}
              name="impactScenariosSearch"
              icon={<SearchIcon />}
              placeholder={t('valueChainMap.impactScenariosSearchPlaceholder')}
              onChange={handleSearchChange}
              className={styles.searchField}
            />
            <Button
              title={t('valueChainMap.startNewAnalysis')}
              labelKey="valueChainMap.startNewAnalysis"
              variant={BUTTON_VARIANT.ghost}
              startIcon={<PlusIcon aria-hidden="true" />}
              onClick={handleStartNewAnalysis}
              isLoading={isCreating}
              className={styles.startAnalysisButton}
            />
            <ImpactScenariosList
              scenarios={filteredScenarios}
              onOpenAnalysis={onOpenAnalysis}
              onDeleteAnalysis={handleDeleteClick}
            />
          </>
        ) : null}
      </div>

      <DeleteModal
        open={pendingDeleteId !== null}
        onReject={handleDeleteReject}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        title="deleteImpactScenario"
      />

      <div className={styles.footer}>
        <Button
          title={t('common.close')}
          labelKey="common.close"
          variant={BUTTON_VARIANT.cancel}
          className={styles.closeButton}
          onClick={onClose}
        />
      </div>
    </SidePanel>
  );
}

export default ImpactScenariosSidebar;
